import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('auth_token'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check authentication on mount
  const checkAuth = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setError(null);
    } catch (err) {
      console.error('Auth check failed:', err);
      // Only clear token on explicit 401 (unauthorized), not on network errors
      if (err.response && err.response.status === 401) {
        localStorage.removeItem('auth_token');
        setToken(null);
        setUser(null);
      }
      // On network error, keep the token - server might just be busy
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Check for impersonation on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const impersonateData = urlParams.get('impersonate');
    
    if (impersonateData) {
      try {
        const { token: impToken, user: impUser, originalAdmin } = JSON.parse(decodeURIComponent(impersonateData));
        
        // Store impersonation token
        sessionStorage.setItem('impersonation_token', impToken);
        sessionStorage.setItem('impersonation_user', JSON.stringify(impUser));
        sessionStorage.setItem('original_admin', originalAdmin);
        
        // Set the token and user
        setToken(impToken);
        setUser({ ...impUser, is_impersonated: true, impersonated_by: originalAdmin });
        axios.defaults.headers.common['Authorization'] = `Bearer ${impToken}`;
        
        // Clear URL parameter
        window.history.replaceState({}, document.title, window.location.pathname);
        
        setLoading(false);
      } catch (e) {
        console.error('Error parsing impersonation data:', e);
      }
    }
  }, []);

  useEffect(() => {
    // Check for existing impersonation session
    const impToken = sessionStorage.getItem('impersonation_token');
    const impUser = sessionStorage.getItem('impersonation_user');
    const originalAdmin = sessionStorage.getItem('original_admin');
    
    if (impToken && impUser) {
      setToken(impToken);
      setUser({ ...JSON.parse(impUser), is_impersonated: true, impersonated_by: originalAdmin });
      axios.defaults.headers.common['Authorization'] = `Bearer ${impToken}`;
      setLoading(false);
    } else {
      checkAuth();
    }
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
      });

      const { access_token, user: userData } = response.data;
      localStorage.setItem('auth_token', access_token);
      setToken(access_token);
      setUser(userData);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const register = async (name, email, password, acceptPrivacy, newsletter = false) => {
    try {
      setError(null);
      const response = await axios.post(`${API}/auth/register`, {
        name,
        email,
        password,
        accept_privacy: acceptPrivacy,
        newsletter: newsletter,
      });

      // New flow: registration now requires email verification
      // Backend returns message and email_sent flag instead of token
      if (response.data.email_sent) {
        return { 
          success: true, 
          requiresVerification: true,
          message: response.data.message,
          email: response.data.email
        };
      }
      
      // Fallback for old flow (if backend returns token)
      const { access_token, user: userData } = response.data;
      if (access_token) {
        localStorage.setItem('auth_token', access_token);
        setToken(access_token);
        setUser(userData);
      }
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Registration failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    sessionStorage.removeItem('impersonation_token');
    sessionStorage.removeItem('impersonation_user');
    sessionStorage.removeItem('original_admin');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  // End impersonation - close the tab or window
  const endImpersonation = () => {
    sessionStorage.removeItem('impersonation_token');
    sessionStorage.removeItem('impersonation_user');
    sessionStorage.removeItem('original_admin');
    window.close();
  };

  const isAuthenticated = !!user && !!token;
  const isAdmin = user?.is_admin || user?.role === 'superadmin' || user?.role === 'worker' || false;
  const isSuperadmin = user?.is_superadmin || user?.role === 'superadmin' || false;
  const isWorker = user?.role === 'worker' || false;
  const isImpersonated = user?.is_impersonated || false;

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        error,
        isAuthenticated,
        isAdmin,
        isSuperadmin,
        isWorker,
        isImpersonated,
        login,
        register,
        logout,
        endImpersonation,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
